/**
 * ProductLandingMode — full-screen conversion overlay for direct product deep links.
 * Renders when a visitor arrives via /m/:slug/product/:productSlug--:id.
 * Fires Facebook Pixel: ViewContent on mount, AddToCart + InitiateCheckout on buy.
 */
import { useEffect } from "react";
import { X, ShoppingBag, Share2, Copy, Clock } from "lucide-react";
import { toast } from "sonner";
import { trackPixelEvent } from "@/hooks/useAnalytics";
import { trackViewProduct, trackStartCheckout } from "@/utils/tracking";
import { resolveMedia } from "@/utils/mediaUtils";

export function ProductLandingMode({ product, restaurant, fmt, tc, isDark, addToCart, openCheckout, onClose, onCustomize, offerPreview }) {
  const pc = tc?.primary || "#059669";
  const cardBg = isDark ? "#111111" : "#ffffff";
  const textMain = isDark ? "#f3f4f6" : "#111827";
  const textSub = isDark ? "rgba(255,255,255,0.6)" : "#6b7280";
  const currency = restaurant?.currency || "EUR";

  const hasRequiredVariations = (product?.variation_groups || []).some(g => g.required);
  const hasDiscount = product?.original_price > 0 && product?.original_price > product?.price;
  const discountPct = hasDiscount ? Math.round((1 - product.price / product.original_price) * 100) : 0;
  const offerApplied = offerPreview?.valid === true && Number(offerPreview?.discount_amount || 0) > 0;
  const displayedPrice = offerApplied ? Number(offerPreview.final_total) : Number(product?.price || 0);

  // ViewContent — fires when landing page is shown (with event_id for dedup)
  useEffect(() => {
    if (!product) return;
    trackViewProduct({
      product_id: product.id,
      product_name: product.name,
      cart_value: product.price,
    });
  }, [product?.id]); // eslint-disable-line

  const handleBuyNow = () => {
    if (hasRequiredVariations) {
      // Fall back to ProductCard modal for variation selection
      onCustomize?.();
      return;
    }
    addToCart(product);  // fires AddToCart pixel + CAPI internally
    trackStartCheckout({ product_id: product.id, cart_value: product.price });
    openCheckout?.();
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success("Link copiado!");
    } catch { toast.error("Erro ao copiar link"); }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: product?.name, url: window.location.href }); } catch {}
    } else {
      await handleCopy();
    }
  };

  if (!product) return null;

  return (
    <div
      className="fixed inset-0 z-[1500] flex flex-col"
      style={{ backgroundColor: "rgba(0,0,0,0.93)" }}
      data-testid="product-landing-mode"
    >
      {/* ── Header ── */}
      <div className="flex justify-between items-center px-4 py-3 shrink-0">
        <button
          onClick={onClose}
          className="flex items-center gap-1.5 text-white/60 hover:text-white text-sm font-medium transition"
        >
          <X className="w-4 h-4" /> Ver menu
        </button>
        <div className="flex gap-1">
          <button onClick={handleCopy} className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-white/10 transition" aria-label="Copiar link">
            <Copy className="w-4 h-4 text-white/60" />
          </button>
          <button onClick={handleShare} className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-white/10 transition" aria-label="Partilhar">
            <Share2 className="w-4 h-4 text-white/60" />
          </button>
        </div>
      </div>

      {/* ── Product image ── */}
      <div className="relative w-full shrink-0 overflow-hidden" style={{ maxHeight: "42vh" }}>
        {product.image_url ? (
          <>
            <img src={resolveMedia(product.image_url)} alt={product.name} className="w-full object-cover" style={{ maxHeight: "42vh" }} />
            <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, transparent 40%, rgba(0,0,0,0.9))" }} />
          </>
        ) : (
          <div className="w-full flex items-center justify-center text-7xl" style={{ height: "22vh", backgroundColor: `${pc}18` }}>🍽️</div>
        )}
      </div>

      {/* ── Product info ── */}
      <div className="flex-1 overflow-y-auto px-4 pt-4 pb-2">
        <div className="rounded-2xl p-5 space-y-3" style={{ backgroundColor: cardBg }}>
          {restaurant?.name && (
            <p className="text-[11px] font-bold uppercase tracking-widest" style={{ color: pc }}>{restaurant.name}</p>
          )}
          <h1 className="text-2xl font-black leading-tight" style={{ color: textMain }}>{product.name}</h1>
          <div className="flex items-baseline gap-3 flex-wrap">
            <span className="text-3xl font-black" style={{ color: pc }}>{fmt(displayedPrice)}</span>
            {offerApplied && <span className="text-base line-through" style={{ color: textSub }}>{fmt(product.price)}</span>}
            {!offerApplied && hasDiscount && <span className="text-base line-through" style={{ color: textSub }}>{fmt(product.original_price)}</span>}
            {!offerApplied && hasDiscount && <span className="text-xs bg-red-500 text-white font-bold px-2 py-0.5 rounded-full">-{discountPct}%</span>}
            {offerApplied && (
              <span className="text-xs bg-red-500 text-white font-bold px-2 py-0.5 rounded-full" data-testid="landing-coupon-badge">
                Cupom {offerPreview.code}
              </span>
            )}
          </div>
          {product.description && (
            <p className="text-sm leading-relaxed" style={{ color: textSub }}>{product.description}</p>
          )}
          {product.preparation_time > 0 && (
            <div className="flex items-center gap-1.5 text-xs" style={{ color: textSub }}>
              <Clock className="w-3.5 h-3.5" />~{product.preparation_time} min de preparo
            </div>
          )}
        </div>
      </div>

      {/* ── Sticky CTA ── */}
      <div className="shrink-0 px-4 pb-6 pt-3 space-y-2.5" style={{ backgroundColor: "rgba(0,0,0,0.95)" }}>
        <button
          onClick={handleBuyNow}
          className="w-full h-16 rounded-2xl font-black text-lg text-white flex items-center justify-center gap-3 active:scale-[0.98] transition-transform"
          style={{ backgroundColor: pc, boxShadow: `0 8px 28px ${pc}60` }}
          data-testid="landing-buy-now-btn"
        >
          <ShoppingBag className="w-5 h-5" />
          {hasRequiredVariations ? "Personalizar e comprar" : `Comprar agora · ${fmt(displayedPrice)}`}
        </button>
        <div className="flex gap-2">
          <button
            onClick={handleCopy}
            className="flex-1 h-10 rounded-xl border font-semibold text-sm flex items-center justify-center gap-1.5 text-white/70 border-white/20 hover:bg-white/10 transition"
          >
            <Copy className="w-3.5 h-3.5" /> Copiar link
          </button>
          <button
            onClick={handleShare}
            className="flex-1 h-10 rounded-xl border font-semibold text-sm flex items-center justify-center gap-1.5 text-white/70 border-white/20 hover:bg-white/10 transition"
          >
            <Share2 className="w-3.5 h-3.5" /> Partilhar
          </button>
        </div>
      </div>
    </div>
  );
}
